import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { verifyAuthToken } from "../../services/authService";
import { setProfile } from "./profileSlice";

// Counter to track the latest verifyAuth request and discard stale results
let verifyAuthCounter = 0;

const initialState = {
  session: { user: null, status: "loading", error: null },
  loginPending: false,
  loginError: null,
  loginData: null,
  verifyAuthRequestId: null, // tracks which verifyAuth call is the latest
};

// Thunk for verifying auth token
export const verifyAuth = createAsyncThunk(
  "auth/verifyAuth",
  async (_, { dispatch }) => {
    // Increment counter and capture this call's ID
    const thisRequestId = ++verifyAuthCounter;

    // No token, nothing to verify. The API only reads the Authorization
    // header, so the call could only come back 401 invalidToken — and the
    // response interceptor answers that with sessionStorage.clear(), which
    // wiped per-tab markers such as etsy_finished on every public page load.
    if (!localStorage.getItem("auth_token")) {
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: null,
        requestId: thisRequestId,
      };
    }

    const res = await verifyAuthToken();
    const accessToken = localStorage.getItem("auth_token");

    // If a newer verifyAuth was dispatched while this one was in-flight, discard this result
    if (thisRequestId !== verifyAuthCounter) {
      return { _stale: true };
    }

    if (res?.status === 200) {
      dispatch(setProfile({ ...res?.data, access_token: accessToken }));
      return {
        user: { ...res?.data, access_token: accessToken },
        status: "authorized",
        error: null,
        requestId: thisRequestId,
      };
    } else if (res?.data?.invalidToken) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refreshToken");
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: "Invalid token",
        requestId: thisRequestId,
      };
    } else {
      // Non-200 but NOT invalidToken — could be a transient error.
      // Only mark unauthorized if there's genuinely no token in localStorage.
      const tokenStillExists = !!localStorage.getItem("auth_token");
      if (tokenStillExists) {
        // Token exists but verify failed (server hiccup) — keep current session
        return { _stale: true };
      }
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: null,
        requestId: thisRequestId,
      };
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession: (state, action) => {
      state.session = { ...state.session, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // verifyAuth: preserve current session during re-verification (no flash to loading)
      .addCase(verifyAuth.pending, (state) => {
        // On first load (status is still "loading"), keep loading.
        // On subsequent re-verifications, keep the current session intact.
        // This prevents the UI from flashing to loading/unauthorized during navigation.
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        // Ignore stale results from superseded verifyAuth calls
        if (action.payload._stale) return;
        state.session = {
          user: action.payload.user,
          status: action.payload.status,
          error: action.payload.error,
        };
      })
      .addCase(verifyAuth.rejected, (state) => {
        // Network error or unexpected failure — only mark unauthorized if no token
        const tokenExists = !!localStorage.getItem("auth_token");
        if (!tokenExists) {
          state.session = { user: null, status: "unauthorized", error: null };
        }
        // If token exists, keep current session (transient error)
      });
  },
});

export const { setSession } = authSlice.actions;
export default authSlice.reducer;
